import { useCallback, type JSX } from "react";
import { useSearchParams } from "react-router-dom";
import { EmptyState } from "@mailflare/ui";
import type { MessageId } from "@mailflare/shared";

import { useAppShell } from "@/app/layout/app-shell";

import { useSearch } from "@/features/search/hooks";
import { parseSearchParams } from "@/features/search/params";
import { MailWorkspace } from "./mail-workspace";

export function SearchPage(): JSX.Element {
  const { mailbox } = useAppShell();
  const [searchParams, setSearchParams] = useSearchParams();
  const args = parseSearchParams(searchParams);
  const query = useSearch(args ?? { q: "" }, args !== null);
  const selectedId = (searchParams.get("message") as MessageId | null) ?? null;

  const setSelected = useCallback(
    (id: MessageId | null): void => {
      const next = new URLSearchParams(searchParams);
      if (id === null) next.delete("message");
      else next.set("message", id);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  if (mailbox === null) {
    return (
      <EmptyState
        title="Mailbox not found"
        description="This mailbox is unavailable."
        className="m-auto"
      />
    );
  }

  if (args === null) {
    return (
      <EmptyState
        title="Search your mail"
        description="Type a query in the search bar above and press Enter."
        className="m-auto"
      />
    );
  }

  const messages = query.data?.messages ?? [];
  const tookMs = query.data?.tookMs;

  return (
    <MailWorkspace
      mailbox={mailbox}
      messages={messages}
      listLabel={tookMs !== undefined ? `Search results (${tookMs} ms)` : "Search results"}
      emptyTitle="No results"
      emptyDescription={`Nothing matched “${args.q}”. Try different keywords or filters.`}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      hasNextPage={query.data?.pageInfo.hasMore ?? false}
      isFetchingNextPage={false}
      onLoadMore={() => undefined}
      selectedId={selectedId}
      onSelect={setSelected}
    />
  );
}
